import { useEffect, useState } from "react";
import { Row, Col, Stack, Card, Button, Spinner } from "react-bootstrap";
import { AppIcon } from "@/components/Icon";
import { Link, useParams } from "react-router-dom";
import { fetchSupportTicketSummary } from "@/services";

export default function SupportSection({ fillHeight = false }) {
  const { loanUUID: loanId } = useParams();

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSummary();
  }, []);

  const loadSummary = async () => {
    try {
      const res = await fetchSupportTicketSummary(loanId);
      const data = res?.results || res?.data || res;
      setSummary(data);
    } catch (err) {
      console.error("Failed to fetch ticket summary", err);
    } finally {
      setLoading(false);
    }
  };

  const getTicketText = () => {
    if (!summary || summary.total === 0) return null;

    const underReview = summary.under_review || 0;
    const resolved = summary.resolved || 0;

    return {
      primary:
        underReview > 0
          ? `You have ${underReview} ticket${underReview > 1 ? "s" : ""} under review`
          : `You have ${resolved} ticket${resolved > 1 ? "s" : ""}`,
      secondary:
        resolved > 0
          ? `${resolved} ticket${resolved > 1 ? "s" : ""} resolved`
          : null,
    };
  };

  const ticketText = getTicketText();

  return (
    <Card
      className="border-0 rounded-lg p-4"
      style={fillHeight ? { minHeight: "100%" } : undefined}
    >
      <Stack gap={4}>
        {/* Icon */}
        <div
          className="rounded-circle d-flex align-items-center justify-content-center"
          style={{
            width: 64,
            height: 64,
            background: "#F2F3F5",
          }}
        >
          <AppIcon name="Headphones" size={32} className="text-gray-400" />
        </div>

        {/* Text */}
        <Stack gap={1}>
          <div className="fw-semibold text-lg text-black">
            Looking for help?
          </div>
          <div className="text-sm text-gray-500">
            Contact us by creating a ticket now.
          </div>
        </Stack>

        {/* Primary CTA */}
        <Link to={`/lms/${loanId}/tickets`} style={{ textDecoration: "none" }}>
          <Button className="bg-black border-0 w-100 fw-medium py-2">
            Open a ticket
          </Button>
        </Link>

        {/* ✅ Ticket Summary Box */}
        {!loading && ticketText && (
          <Link
            to={`/lms/${loanId}/tickets`}
            style={{ textDecoration: "none" }}
          >
            <div
              className="d-flex align-items-center justify-content-between"
              style={{
                background: "#F8F8FB",
                padding: "14px 16px",
                borderRadius: 10,
                fontSize: 14,
                fontWeight: 500,
                color: "#111",
              }}
            >
              <div className="d-flex gap-2">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center"
                  style={{
                    width: 24,
                    height: 24,
                    background: "#FFF3D6",
                    marginTop: 2,
                  }}
                >
                  <AppIcon
                    name="Clock"
                    size={14}
                    className="text-warning"
                  />
                </div>

                <div>
                  <div>{ticketText.primary}</div>
                  {ticketText.secondary && (
                    <div
                      style={{
                        fontSize: 12,
                        color: "#6B7280",
                        marginTop: 2,
                      }}
                    >
                      {ticketText.secondary}
                    </div>
                  )}
                </div>
              </div>

              <AppIcon name="ArrowRight" className="text-gray-400" />
            </div>
          </Link>
        )}

        {/* Optional loading state */}
        {loading && (
          <div className="text-sm text-gray-400 d-flex align-items-center gap-2">
            <Spinner size="sm" />
            Loading tickets…
          </div>
        )}
      </Stack>
    </Card>
  );
}